import ListenerItem from '../../../../utils/ListenerItem';
import Color from '../../../../utils/Color';
import DomEventHelper from '../../../../utils/DomEventHelper';
import Validator from '../../../../utils/Validator';
import BscMgr from '../../../../gui/BscMgr';
import PSA from '../../../../psa';
import Utils from '../../../../utils/Utils';
import XtwCol from '../../parts/XtwCol';
import MenuHandler from '../../../../gui/menu/MenuHandler';
import MnuObj from '../../../../gui/menu/MnuObj';
import XtwHead from '../../XtwHead';

import { MI_ANY_CLICKED, MI_TEXT_CLICKED } from '../../../../gui/menu/MenuHandler';
import UIRefresh from '../../../../utils/UIRefresh';

const ID_SELECTALL = -1000;
const HEADER_CONTEXT_MENU_CLASSNAME = "rtp-header-contextmenu";

class XHMenuHandler extends MenuHandler {

	/**
	 * constructs a new instance
	 * @param {XtwHeadContextMenuExtension} ext the context menu extension
	 */
	constructor(ext) {
		super('widgets.xtw.XHMenuHandler');
		this._ext = ext;
	}

	/**
	 * @returns {XtwHeadContextMenuExtension} the context menu extension
	 */
	get ext() {
		return this._ext;
	}

	/**
	 * @inheritdoc
	 * @override
	 */
	doDestroy() {
		this._ext = null;
		super.doDestroy();
	}

	/**
	 * @param {Number} id the ID of the menu item that was clicked
	 * @param {MnuObj} menu the menu that contains this item
	 * @param {Number} what an indicator which part was clicked:
	 * @inheritdoc
	 * @override
	 */
	onMenuItem(id, menu, what) {
		if ( this.alive ) {
			this.ext.onMenuItem(id, menu, what);
		}
	}

	/**
     * @param {MnuObj} menu the menu that was closed
	 * @inheritdoc
	 * @override
	 */
	onMenuClose(menu) {
		if ( this.alive ) {
			this.ext.onMenuClose(menu);
		}
	}

	/**
	 * the searchable menu asks its active handler for the search input placeholder text; the
	 * header context menu provides the column-search title configured on the host header
	 * @returns {String | undefined} the placeholder text, or undefined if none is configured
	 * @inheritdoc
	 * @override
	 */
	get searchInputPlaceholder() {
		return this.alive && this.ext && Validator.isString( this.ext.host.searchInColumnsMenuOptionTitle ) ?
			this.ext.host.searchInColumnsMenuOptionTitle : void 0;
	}
}


/**
 * class XtwHeadContextMenuExtension - provides the header context menu (column
 * visibility) behavior for an {@link XtwHead}. It is a plain delegate: the host header is
 * passed in and accessed via {@link #host}; the extension owns the field menu and the menu
 * handler.
 * <p>
 * Keyboard navigation and item focus inside the menu are handled generically by the
 * searchable menu ({@link SearchableMenuObject}); this extension only provides the
 * column-visibility items, the "keep menu open on right-click toggle" behavior and the
 * search input placeholder.
 */
export default class XtwHeadContextMenuExtension extends ListenerItem {

	/**
	 * constructs a new instance
	 * @param {XtwHead} host the host table header (XtwHead)
	 */
	constructor( host ) {
		super( 'widgets.xtw.contextmenu.XtwHeadContextMenuExtension' );
		this._host = host;
		/** the field menu (column visibility menu), created on demand */
		this.fieldMenu = null;
		// create menu handler
		this._menuHandler = new XHMenuHandler( this );
	}

	/**
	 * @returns {XtwHead} the host table header
	 */
	get host() {
		return this._host;
	}

	/**
	 * the host header's DOM element. Exposed because this extension registers its own
	 * event listeners (via {@link ListenerItem#addListener}), which bind to this element.
	 * @returns {HTMLElement|undefined} the host's DOM element
	 */
	get element() {
		return this.host.element;
	}

	/**
	 * @returns {Boolean} true if the host header is rendered
	 */
	get isRendered() {
		return this.host.element instanceof HTMLElement;
	}

	/**
	 * @override
	 */
	doDestroy() {
		this._dropFieldMenu();
		if ( this._menuHandler ) {
			this._menuHandler.destroy();
			this._menuHandler = null;
		}
		this._host = void 0;
		super.doDestroy();
	}

	/**
	 * @returns {MenuHandler} the menu handler
	 */
	get menuHandler() {
		return this._menuHandler;
	}

	get allColumns() {
		const columns = this.host.columns;
		if ( !Validator.isObject( columns ) || !Validator.isMap( columns._objReg ) ) {
			return [];
		}
		return [ ...columns._objReg.values() ];
	}

	/**
	 * @returns {Array<XtwCol>} an array of all available columns
	 */
	get allAvailableColumns() {
		return this.allColumns.filter( column => Validator.isObject( column ) && !!column.available && !column.select );
	}

	get allAvailableVisibleColumns() {
		return this.allColumns.filter( column => Validator.isObject( column ) && !!column.available && !!column.visible && !column.select );
	}

	get allAvailableHiddenColumns() {
		return this.allColumns.filter( column => Validator.isObject( column ) && !!column.available && !column.visible && !column.select );
	}

	get allAvailableColumnsAreVisible() {
		return this.allAvailableColumns.every( column => column.visible );
	}

	get headerContextMenuElement() {
		return Validator.isObject( this.fieldMenu ) && this.fieldMenu.mnuDiv instanceof HTMLElement ? this.fieldMenu.mnuDiv : null;
	}

	get contextMenuElementContainer() {
		const headerContextMenuElement = this.headerContextMenuElement;
		return !!headerContextMenuElement ? [ ...headerContextMenuElement.children ].find( element => element.style.overflow == "hidden" ) : null;
	}

	get headerContextMenuItems() {
		return !Validator.isObjectPath( this.fieldMenu, "fieldMenu.mnuItems" ) ||
			!Validator.isMap( this.fieldMenu.mnuItems._objReg ) ?
			null : [ ...this.fieldMenu.mnuItems._objReg.values() ].filter( item => Validator.is( item, "MnuItm" ) && !item.inactive && !item.sep );
	}

	get visibleHeaderContextMenuItems() {
		return !Validator.isObject( this.fieldMenu ) || !( "visibleMenuItems" in this.fieldMenu ) ? null : this.fieldMenu.visibleMenuItems;
	}

	get lastHeaderContextMenuClickEventWasRightButton() {
		return Validator.isObjectPath( this.fieldMenu, "fieldMenu.mnuMgr" ) && !!this.fieldMenu.mnuMgr.lastHeaderContextMenuClickEventWasRightButton;
	}

	set lastHeaderContextMenuClickEventWasRightButton( newValue ) {
		if ( !Validator.isObjectPath( this.fieldMenu, "fieldMenu.mnuMgr" ) ) {
			return;
		}
		this.fieldMenu.mnuMgr.lastHeaderContextMenuClickEventWasRightButton = !!newValue;
	}

	voidLastClickEventRightButtonFlag() {
		if ( !Validator.isObjectPath( this.fieldMenu, "fieldMenu.mnuMgr" ) ) {
			return false;
		}
		this.fieldMenu.mnuMgr.lastHeaderContextMenuClickEventWasRightButton = null;
		delete this.fieldMenu.mnuMgr.lastHeaderContextMenuClickEventWasRightButton;
		return true;
	}

	addContextMenuListener() {
		return this.addPrefixListener("contextmenu", "onContextMenu", "XtwHeadContextMenu");
	}

	removeContextMenuListener() {
		return this.removePrefixListener("contextmenu", "XtwHeadContextMenu");
	}

	/**
	 * handles context menu events
	 * @param {MouseEvent} domEvent the context menu event
	 */
	onContextMenu( domEvent ) {
		if ( !Validator.isObject( this.host.xtwBody ) ) {
			return false;
		}
		if ( domEvent instanceof MouseEvent ) {
			domEvent.stopPropagation();
			domEvent.preventDefault();
		}
		const menu = this._getFieldMenu( DomEventHelper.isCtrlEvent( domEvent ) );
		if ( !Validator.isObject( menu ) ) {
			return false;
		}
		const menuManager = PSA.getInst().getMnuMgr();
		menuManager.showMenu( menu, {
			element: this.host.element,
			rect: null
		}, this.menuHandler, true, true );
		this.refreshHeaderContextMenuSearchInputPlaceholder();
		this.focusHeaderContextMenu();
		return true;
	}

	focusHeaderContextMenuSearchInput() {
		return Validator.isFunctionPath( this.fieldMenu, "fieldMenu.focusHeaderContextMenuSearchInput" ) ? this.fieldMenu.focusHeaderContextMenuSearchInput() : false;
	}

	focusHeaderContextMenu() {
		const headerContextMenuElement = this.headerContextMenuElement;
		if ( !headerContextMenuElement ) {
			return false;
		}
		headerContextMenuElement.classList.add( HEADER_CONTEXT_MENU_CLASSNAME );
		headerContextMenuElement.tabIndex = 0;
		headerContextMenuElement.focus();
		return true;
	}

	refreshHeaderContextMenuSearchInputPlaceholder() {
		return Validator.isFunctionPath( this.fieldMenu, "fieldMenu.refreshInputPlaceholder" ) ? this.fieldMenu.refreshInputPlaceholder() : false;
	}

	/**
	 * handles context menu events
	 * @param {MouseEvent} evt the context menu event
	 * @param {XtwCol} column the column that causes this call
	 */
	onHeaderContextMenu( evt, column ) {
		if ( this.host.xtwBody && column && column.element ) {
			const menu = this._getFieldMenu( DomEventHelper.isCtrlEvent( evt ) );
			if ( menu ) {
				PSA.getInst().getMnuMgr().showMenu( menu, { element: column.element, rect: null }, this.menuHandler, true, false );
				this.refreshHeaderContextMenuSearchInputPlaceholder();
				this.focusHeaderContextMenu();
				this.focusHeaderContextMenuSearchInput();
			}
		}
	}

	/**
	 * menu handler callback: called if a menu is closed
	 * @param {MnuObj} menu the menu that was closed
	 */
	onMenuClose( menu ) {
		// drop the field menu
		this._dropFieldMenu();
		// verify, that at least one column is visible
		const cols = this.host.columns;
		const avl_cols = new Map();
		const vis_cols = new Map();
		const fix_cols = new Map();
		try {
			cols.forEach( ( col ) => {
				if ( col.available && !col.select ) {
					avl_cols.set( col.id, col );
					if ( col.visible ) {
						vis_cols.set( col.id, col );
					}
					if ( col.fix ) {
						fix_cols.set( col.id, col );
					}
				}
			} );
			if ( vis_cols.size === 0 ) {
				// ooops?!
				let idc = null;
				if ( fix_cols.size > 0 ) {
					idc = fix_cols.values().next().value.id;
				} else if ( avl_cols.size > 0 ) {
					idc = avl_cols.values().next().value.id;
				}
				if ( idc ) {
					this.onMenuItem( idc, null, MI_ANY_CLICKED );
				}
			}
		} finally {
			fix_cols.clear();
			vis_cols.clear();
			avl_cols.clear();
		}
	}

	/**
	 * menu handler callback: called if a menu item was clicked
	 * @param {Number} id ID of selected menu item
	 * @param {MnuObj} menu the active menu
     * @param {Number} what an indicator which part of the menu item was clicked:
     *                      0 - unspecified;
     *                      1 - the icon was clicked;
     *                      2 - the text was clicked
	 */
	onMenuItem( id, menu, what ) {
		let success = false;
		const self = this;
		const showBlockScreen = new Promise( ( resolve, reject ) => {
			BscMgr.showBlockScreen( true );
			resolve( true );
		} );
		setTimeout( () => {
			const menuItemToggled = showBlockScreen.then( () => {
				UIRefresh.runDelayed( () => {
					success = id === ID_SELECTALL ?
						self.toggleAllContextMenuItems( menu ) :
						self.toggleOneContextMenuItem( id, menu, what );
				} );
			} );
			menuItemToggled.then( () => BscMgr.hideBlockScreen() );
		}, 0 );
		return success;
	}

	toggleOneContextMenuItem( columnId, menu, what ) {
		const columns = this.host.columns;
		if ( !Validator.isFunctionPath( columns, "columns.getObj" ) ) {
			return false;
		}
		const targetColumn = columns.getObj( columnId );
		if ( !(targetColumn instanceof XtwCol) || !targetColumn.available ) {
			return false;
		}
		const columnVisibleBeforeToggle = !!targetColumn.visible;
		const all_cols = this.allAvailableColumns;
		let vc = 0;
		all_cols.forEach(c => {
			if ( c.visible ) {
				++vc;
			}
		});
		if ( (vc === 1) && columnVisibleBeforeToggle ) {
			// No, Sir! At least one column should remain visible
			return false;
		}
		if ( what === MI_TEXT_CLICKED ) {
			// clicked on the text
			if ( !columnVisibleBeforeToggle ) {
				// make the column visible!
				this.host.toggleColumnVisibility( columnId );
			}
			// else: do *nothing*! just inform the web server
		} else {
			// *not* clicked on the text --> just toggle visibility
			this.host.toggleColumnVisibility( columnId );
		}
		const allAvailableColumnsAreVisible = this.allAvailableColumnsAreVisible;
		if ( Validator.isObject( menu ) && !!menu.alive ) {
			menu.checkItem( columnId, !!targetColumn.visible );
			menu.checkItem( ID_SELECTALL, allAvailableColumnsAreVisible );
		}
		this.host._nfySrv( 'columnVisibility', {
			idc: columnId,
			all: false,
			mode: what,
			org: columnVisibleBeforeToggle,
			vis: targetColumn.visible
		} );
		const self = this;
		this.host._setRfrCallback(() => {
			self.refreshUiAfterColumnVisibilityChange(!columnVisibleBeforeToggle ? targetColumn : null);
		});
		return true;
	}

	toggleAllContextMenuItems( menu ) {
		const setAllColumnsToVisible = !this.allAvailableColumnsAreVisible;
		const targetedColumns = setAllColumnsToVisible ? this.allAvailableHiddenColumns : this.allAvailableVisibleColumns;
		if ( !Validator.isArray( targetedColumns, true ) ) {
			return false;
		}
		const onlyVisibleColumn = !setAllColumnsToVisible ? targetedColumns.shift() : null;
		const menuIsValid = Validator.isObject( menu ) && !!menu.alive;
		for ( let column of targetedColumns ) {
			this.host.toggleColumnVisibility( column.id );
			if ( menuIsValid ) {
				menu.checkItem( column.id, column.visible );
			}
		}
		this.host._nfySrv( 'columnVisibility', {
			idc: -1,
			all: true,
			vis: setAllColumnsToVisible
		} );
		if ( !setAllColumnsToVisible && onlyVisibleColumn ) {
			this.host._nfySrv( 'columnVisibility', {
				idc: onlyVisibleColumn.id,
				all: false,
				org: false,		// that's what the backend assumes at this point
				vis: true
			} );
		}
		if ( menuIsValid ) {
			menu.checkItem( ID_SELECTALL, setAllColumnsToVisible );
		}
		const self = this;
		this.host._setRfrCallback(() => {
			self.refreshUiAfterColumnVisibilityChange(null);
		});
		return true;
	}

	/**
	 * refreshes the UI after the visibility of a column was changed
	 * @param {XtwCol | null} columnToScrollTo the column to scroll to
	 */
	refreshUiAfterColumnVisibilityChange( columnToScrollTo ) {
		this.host._renderAllCols();
		this.host.xtwBody.rebuildAllRows();
		if ( (columnToScrollTo instanceof XtwCol) && !!columnToScrollTo.visible ) {
			this.host.scrollToColumn( columnToScrollTo, false );
			if ( this.lastHeaderContextMenuClickEventWasRightButton ) {
				this.host.xtwBody.setupAfterScrollViewUpdateCallback(
					"refreshUiAfterColumnVisibilityChange-", () => {
						this.focusHeaderContextMenu();
					} );
			}
		}
		if ( this.host.xtdTbl ) {
			// "auto fit" columns if required
			this.host.xtdTbl.autoFitColumns();
		}
		const scbHorz = this.host.scbHorz;
		if ( Validator.isObject( scbHorz ) ) {
			// force the table body to update the scrolling position
			this.host.xtwBody.resetHScroll();
			// at this point the scrollbar widget is already updated - we force a scroll notification
			const current = scbHorz._selection;
			scbHorz._selection = current + 1;
			scbHorz.setSelection( current );
		}
		this.voidLastClickEventRightButtonFlag();
	}

	_dropFieldMenu() {
		if ( this.fieldMenu ) {
			const mnu = this.fieldMenu;
			this.fieldMenu = null;
			mnu.destroy();
		}
	}

	/**
	 * returns the field menu; creates it if necessary
	 * @param {Boolean} use_dsc flag whether to use field descriptors instead of regular field names
	 * @returns {MnuObj} the field menu
	 */
	_getFieldMenu( use_dsc ) {
		if ( this.fieldMenu || !this.host.xtwBody ) {
			return this.fieldMenu;
		}
		const selectAllItemTitle = this.host.selAllTtl;
		const allAvailableColumnsAreVisible = this.allAvailableColumnsAreVisible;
		const post_create = ( items ) => {
			this.doAfterCreatingHeaderContextMenuItems(items, selectAllItemTitle, allAvailableColumnsAreVisible );
		};
		this.fieldMenu = this.host.xtwBody.createFieldMenu( false, null, null, post_create, !!use_dsc );
		// keyboard navigation and item focus are handled generically by the searchable
		// menu; the header only needs its "keep menu open on right-click toggle" hook
		this.decorateHeaderContextMenuItemsWithClickControl( this.headerContextMenuItems );
		return this.fieldMenu;
	}

	doAfterCreatingHeaderContextMenuItems(
		menuItems, selectAllItemTitle, areAllColumnsVisible ) {
		if ( !Validator.isIterable( menuItems ) ) {
			return false;
		}
		menuItems.forEach( menuItem => {
			const column = this.host.columns.getObj( menuItem.id );
			if ( column && column.visible ) {
				menuItem.chk = true;
			}
			menuItem.rgtclk = true; // we want right click events!
		} );
		this.addTwoSpecialHeaderContextMenuItems(
			menuItems, selectAllItemTitle, areAllColumnsVisible );
		return true;
	}

	/**
	 * decorates the menu items with the "keep menu open on right-click toggle" click control;
	 * keyboard navigation and item focus are handled generically by the searchable menu
	 * @param {Array} menuItems the menu items
	 * @returns {Boolean} true if successful
	 */
	decorateHeaderContextMenuItemsWithClickControl( menuItems ) {
		if ( !Validator.isIterable( menuItems ) ) {
			return false;
		}
		menuItems.forEach( menuItem => {
			this.overrideOnClickMethodOnHeaderContextMenuItem( menuItem );
		} );
		return true;
	}

	overrideOnClickMethodOnHeaderContextMenuItem( menuItem ) {
		if ( !Validator.isFunctionPath( menuItem, "menuItem._onClick" ) ) {
			return false;
		}
		if ( Validator.isFunction( menuItem._onOriginalClick ) ) {
			return true;
		}
		menuItem._onOriginalClick = menuItem._onClick;
		menuItem._onClick = domEvent => {
			if ( !Validator.isObject( menuItem ) ) {
				return false;
			}
			const isRightClick = domEvent instanceof MouseEvent && domEvent.button === 2;
			const onOriginalClickFunctionIsValid =
				Validator.isFunction( menuItem._onOriginalClick );
			if ( Validator.isObject( menuItem.mnuMgr ) ) {
				if ( !onOriginalClickFunctionIsValid || !isRightClick ) {
					menuItem.mnuMgr.lastHeaderContextMenuClickEventWasRightButton = void 0;
					delete menuItem.mnuMgr.lastHeaderContextMenuClickEventWasRightButton;
				} else {
					menuItem.mnuMgr.lastHeaderContextMenuClickEventWasRightButton = true;
				}
			}
			if ( !onOriginalClickFunctionIsValid ) {
				return false;
			}
			menuItem._onOriginalClick( domEvent );
			return true;
		}
		menuItem.element.removeEventListener( "click", menuItem.lsrClk );
		menuItem.lsrClk = Utils.bind( menuItem, menuItem._onClick );
		menuItem.element.addEventListener( "click", menuItem.lsrClk, false );
		return true;
	}

	addTwoSpecialHeaderContextMenuItems(
		menuItems, selectAllItemTitle, areAllColumnsVisible ) {
		if ( !Validator.isIterable( menuItems ) ||
			!Validator.isString( selectAllItemTitle ) ) {
			return false;
		}
		menuItems.unshift( {
			id: ID_SELECTALL,
			txt: selectAllItemTitle,
			img: null,
			ttlImg: null,
			ina: false,
			stc: false,
			ena: true,
			chk: !!areAllColumnsVisible,
			rgtclk: true,
			items: null
		}, { id: -1, stc: true } );
		return true;
	}

}
